import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, ChevronRight } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { IconButton, Button } from './ui/Button';
import {
  listWorkbookReviewInbox,
  markWorkbookReviewInboxRead,
  type WorkbookReviewInboxItem,
} from '../services/api';

const notificationHeadline = (item: WorkbookReviewInboxItem) => {
  const checkpoint = item.payload.checkpoint_key ?? 'spreadsheet checkpoint';
  return `Review requested: ${checkpoint.replaceAll('_', ' ')}`;
};

/**
 * Global notification entry point for actionable spreadsheet reviews.  It is
 * intentionally mounted above routes so the answerable review is never hidden
 * by a product-specific page shell.
 */
const WorkbookReviewNotifications: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [headerReady, setHeaderReady] = React.useState(false);

  // The app shell is route-lazy-loaded. Do not flash a detached global bell
  // before the route has mounted its header; also reset it while navigating to
  // the next route so it only appears alongside the visible header.
  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.getElementById('root');
    if (!root) return;

    const syncHeaderReady = () => {
      setHeaderReady(Boolean(root.querySelector('nav.app-header')));
    };
    syncHeaderReady();

    const observer = new MutationObserver(syncHeaderReady);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  const inboxQuery = useQuery({
    queryKey: ['workbook-review-inbox'],
    queryFn: () => listWorkbookReviewInbox(),
    enabled: Boolean(user),
    refetchInterval: 30_000,
  });
  const actionable = (inboxQuery.data?.items ?? []).filter(
    (item) => !item.resolved_at
  );
  const actionableCount = actionable.length;
  const markRead = useMutation({
    mutationFn: (inboxId: string) => markWorkbookReviewInboxRead(inboxId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['workbook-review-inbox'] }),
  });

  // The embedded spreadsheet iframe has its own React root. The parent plan
  // workspace owns the global bell, so do not duplicate it in the preview.
  if (typeof window !== 'undefined' && window.self !== window.top) return null;
  if (isAuthLoading || !user || !headerReady) return null;

  return (
    <div className="fixed right-5 top-3 z-[70]">
      <IconButton
        label={
          actionableCount
            ? `${actionableCount} spreadsheet review notification${actionableCount === 1 ? '' : 's'} awaiting action`
            : 'Spreadsheet review notifications'
        }
        variant="ghost"
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 ${
          actionableCount
            ? 'border-primary-400 bg-primary-500/15 text-primary-200 shadow-[0_0_18px_rgba(190,24,93,0.8)]'
            : 'border-border bg-surface text-ink-subtle hover:bg-surface-strong hover:text-ink'
        }`}
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {actionableCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-[11px] font-bold text-white ring-2 ring-background">
            {actionableCount > 9 ? '9+' : actionableCount}
          </span>
        )}
      </IconButton>
      {open && (
        <section
          className="absolute right-0 mt-2 w-[min(24rem,calc(100vw-2.5rem))] overflow-hidden rounded-xl border border-primary-500/45 bg-surface shadow-[0_0_28px_rgba(190,24,93,0.25)]"
          aria-label="Spreadsheet review notifications"
        >
          <header className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">Notifications</h2>
            <p className="mt-0.5 text-xs text-ink-subtle">
              Reviews waiting for your decision
            </p>
          </header>
          {actionable.length ? (
            <ul className="max-h-80 overflow-y-auto p-2">
              {actionable.map((item) => (
                <li key={item.id}>
                  <Button
                    variant="ghost"
                    density="compact"
                    type="button"
                    className={`h-auto w-full justify-start gap-3 rounded-lg px-3 py-3 text-left hover:bg-surface-strong ${
                      item.read_at ? '' : 'bg-primary-500/10'
                    }`}
                    onClick={() => {
                      setOpen(false);
                      if (!item.read_at) markRead.mutate(item.id);
                      if (item.payload.plan_id) {
                        navigate(
                          `/projects/${item.project_id}/spreadsheet-plans/${item.payload.plan_id}`
                        );
                      }
                    }}
                    disabled={!item.payload.plan_id}
                  >
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${item.read_at ? 'bg-border' : 'bg-primary-400 shadow-[0_0_8px_rgba(190,24,93,0.95)]'}`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {notificationHeadline(item)}
                      </span>
                      <span className="mt-0.5 block text-xs text-ink-subtle">
                        Open the live workbook and approve or revise the plan.
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-subtle" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-6 text-center text-sm text-ink-subtle">
              No spreadsheet reviews need your attention.
            </p>
          )}
        </section>
      )}
    </div>
  );
};

export default WorkbookReviewNotifications;
